// Host / URL allowlists for share and embed construction (defense in depth).
// Keep in sync with firestore.rules isAllowed* helpers.

import type { Platform } from './socialUrlParser';

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtu.be',
  'www.youtu.be',
]);

const INSTAGRAM_HOSTS = new Set(['instagram.com', 'www.instagram.com']);

const TWITTER_HOSTS = new Set([
  'twitter.com',
  'www.twitter.com',
  'x.com',
  'www.x.com',
  'mobile.twitter.com',
]);

const SOUNDCLOUD_HOSTS = new Set([
  'soundcloud.com',
  'www.soundcloud.com',
  'm.soundcloud.com',
  'on.soundcloud.com',
]);

function hostnameOf(url: string): string | null {
  try {
    const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(withScheme).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export function isAllowedHostForPlatform(platform: Platform | string, url: string): boolean {
  const host = hostnameOf(url);
  if (!host) return false;

  switch (platform) {
    case 'youtube':
      return YOUTUBE_HOSTS.has(host);
    case 'instagram':
      return INSTAGRAM_HOSTS.has(host);
    case 'twitter':
      return TWITTER_HOSTS.has(host);
    case 'soundcloud':
      return SOUNDCLOUD_HOSTS.has(host);
    default:
      return false;
  }
}

export function isAllowedShareUrl(url: string, platform: Platform | string): boolean {
  if (!url || platform === 'other') return false;
  if (!/^https?:\/\//i.test(url) && !hostnameOf(url)) return false;
  return isAllowedHostForPlatform(platform, url);
}

/** Collection names the client is allowed to write/read as post feeds. */
export const ALLOWED_POST_COLLECTIONS = [
  'youtube-posts',
  'instagram-posts',
  'twitter-posts',
  'soundcloud-posts',
] as const;

export type PostCollectionName = (typeof ALLOWED_POST_COLLECTIONS)[number];

export function isAllowedPostCollection(name: string): name is PostCollectionName {
  return (ALLOWED_POST_COLLECTIONS as readonly string[]).includes(name);
}

export function collectionForPlatform(platform: string): PostCollectionName | null {
  const map: Record<string, PostCollectionName> = {
    youtube: 'youtube-posts',
    instagram: 'instagram-posts',
    twitter: 'twitter-posts',
    soundcloud: 'soundcloud-posts',
  };
  return map[platform] ?? null;
}
